import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type VsPowerWeeks } from '@/lib/api';
import { cn } from '@/lib/utils';
import { KILL_GROWTH_CLASS_META } from '@/lib/killGrowth';

/**
 * Player-hub Stats card (Plan 052): own VS points per M of strength against
 * the power class median. No other names.
 */
export default function VsPowerCard({ token }: { token: string }) {
  const [weeks, setWeeks] = useState<VsPowerWeeks>(4);
  const q = useQuery({
    queryKey: ['player-vs-power', token, weeks],
    queryFn: () => api.getSelfReportVsPower(token, weeks),
    enabled: !!token,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const data = q.data;
  const own = data?.own ?? null;
  const meta = own ? KILL_GROWTH_CLASS_META[own.class] : null;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          VS per strength
        </h2>
        <div className="inline-flex rounded border border-slate-800 bg-slate-950 p-0.5 text-[11px]">
          {([4, 12] as const).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWeeks(w)}
              className={cn(
                'rounded px-2 py-0.5',
                weeks === w ? 'bg-slate-700 text-white' : 'text-slate-400',
              )}
            >
              {w}w
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        {q.isLoading ? (
          <div className="text-xs text-slate-500">Loading…</div>
        ) : !own || !meta || !data ? (
          <p className="text-xs text-slate-400">
            Not enough data yet — you need VS scores in {data?.minWeeks ?? 3} of the last{' '}
            {weeks} weeks and power reports close to those weeks.
          </p>
        ) : (
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-3xl font-semibold tabular-nums" style={{ color: meta.color }}>
                {own.ratio.toFixed(2)}
                <span className="ml-1 text-xs font-normal text-slate-400">VS / M</span>
              </div>
              <div className="text-xs tabular-nums text-slate-400">
                median {own.vsMedian.toFixed(0)} M VS · {own.mSum.toFixed(1)} M-Sum ·{' '}
                {own.weeks} weeks
              </div>
              <div className="mt-1 text-xs text-slate-300">
                Class median {data.classMedian?.medianRatio?.toFixed(2) ?? '—'} · you're #
                {own.classRank} of {own.classSize}
              </div>
            </div>
            <div className="text-right">
              <span
                className={cn(
                  'rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                  meta.chip,
                )}
              >
                {meta.label}
              </span>
              {own.index != null && (
                <div
                  className={cn(
                    'mt-1 text-[11px] tabular-nums',
                    own.index >= 100 ? 'text-emerald-300' : 'text-amber-300',
                  )}
                >
                  index {Math.round(own.index)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
